import { useRef, useState } from 'react'
import {
    type GestureResponderEvent,
    type StyleProp,
    StyleSheet,
    View,
    type ViewStyle,
} from 'react-native'
import { Chip, Menu } from 'react-native-paper'
import { useSemanticColors } from '@/constants/SemanticColors'
import {
    getJobStatusColors,
    getJobStatusDisplay,
    JOB_STATUS_ORDER,
    type Job,
    type JobStatus,
    type JobWithRelations,
} from '@/types/jobs'

interface JobStatusBadgeProps {
    job: Job | JobWithRelations
    onChangeStatus: (status: JobStatus) => void
    style?: StyleProp<ViewStyle>
}

export function JobStatusBadge({ job, onChangeStatus, style }: JobStatusBadgeProps) {
    const semantic = useSemanticColors()
    const currentStatus = job.status as JobStatus
    const colors = getJobStatusColors(currentStatus, semantic)

    const [menuVisible, setMenuVisible] = useState(false)
    const menuKeyRef = useRef(0)

    // The badge lives inside the card's navigation Link. Stopping propagation +
    // preventing the default keeps the tap from bubbling up and navigating to the
    // job page (preventDefault is what blocks the Link's web anchor navigation).
    const openMenu = (event: GestureResponderEvent) => {
        event.stopPropagation()
        event.preventDefault()
        setMenuVisible(true)
    }

    const closeMenu = () => {
        menuKeyRef.current += 1
        setMenuVisible(false)
    }

    const selectStatus = (status: JobStatus) => {
        closeMenu()
        if (status !== currentStatus) {
            onChangeStatus(status)
        }
    }

    return (
        <View style={style} onStartShouldSetResponder={() => true}>
            <Menu
                key={menuKeyRef.current}
                visible={menuVisible}
                onDismiss={closeMenu}
                anchor={
                    <Chip
                        mode="flat"
                        onPress={openMenu}
                        style={[styles.badge, { backgroundColor: colors.background }]}
                        textStyle={[styles.badgeText, { color: colors.text }]}
                    >
                        {getJobStatusDisplay(currentStatus)}
                    </Chip>
                }
            >
                {JOB_STATUS_ORDER.map(status => (
                    <Menu.Item
                        key={status}
                        onPress={() => selectStatus(status)}
                        title={getJobStatusDisplay(status)}
                        // Render a check for the current status and a transparent
                        // spacer for the rest so every label lines up at the same x.
                        leadingIcon={status === currentStatus ? 'check' : 'blank'}
                    />
                ))}
            </Menu>
        </View>
    )
}

const styles = StyleSheet.create({
    badge: {
        borderRadius: 4,
    },
    badgeText: {
        fontWeight: '600',
        fontSize: 12,
    },
})
